import { useCallback, useEffect, useState } from "react";
import { BellRing, Download, ExternalLink, FileText, Pencil, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EditorModal } from "@/components/EditorModal";
import { StaffEvaluationRequestForm } from "@/components/StaffEvaluationRequestForm";
import { StageBar } from "./StageBar";

const FEE_ROLES = ["system_administrator", "executive"];
const money = (value) => (value === null || value === undefined ? "" : `$${Number(value).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const day = (value) => (value ? new Date(value.length === 10 ? `${value}T12:00:00` : value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const CLOSE_REASONS = [["withdrawn", "Withdrawn by school"], ["duplicate", "Duplicate request"], ["no_consent", "No consent"], ["other", "Other"]];

const Panel = ({ title, children, testId }) => (
  <section className="pipeline-panel" data-testid={testId}>
    <h3>{title}</h3>
    {children}
  </section>
);

const RateFields = ({ typeOptions, value, onChange, prefix, row }) => {
  const custom = value.rate_item_id === "custom";
  const choices = typeOptions.filter((option) => (!row?.discipline || option.evaluation_types?.includes(row.discipline)) && (option.evaluation_types || []).includes("Screening") === (row?.service_kind === "Screening"));
  const pick = (id) => {
    const item = typeOptions.find((option) => option.id === id);
    onChange({ ...value, rate_item_id: id, custom_name: "", client_fee: item?.client_fee ?? null, provider_fee: item?.provider_fee ?? (item?.client_fee ? Math.round(item.client_fee * 0.7) : null) });
  };
  const setFee = (fee) => onChange({ ...value, client_fee: fee, provider_fee: fee === null ? null : Math.round(fee * 0.7) });
  return (
    <>
      <label>
        <span className="field-label">Rate line</span>
        <select data-testid={`${prefix}-rate-item`} value={value.rate_item_id} onChange={(event) => pick(event.target.value)}>
          <option value="">Keep current</option>
          <option value="custom">Custom rate…</option>
          {choices.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
        </select>
        {choices.length === 0 && <small className="field-hint">No {row?.service_kind?.toLowerCase()} line for {row?.discipline} on this client’s rate list — use Custom rate…</small>}
      </label>
      {custom && (
        <label>
          <span className="field-label">Service name</span>
          <input data-testid={`${prefix}-custom-name`} required value={value.custom_name} onChange={(event) => onChange({ ...value, custom_name: event.target.value })} placeholder="e.g. AT eval" />
        </label>
      )}
      {(custom || value.rate_item_id) && (
        <>
          <label>
            <span className="field-label">Client fee</span>
            <input data-testid={`${prefix}-client-fee`} type="number" min="0" step="1" value={value.client_fee ?? ""} onChange={(event) => setFee(event.target.value === "" ? null : Number(event.target.value))} />
          </label>
          <label>
            <span className="field-label">Provider fee</span>
            <input data-testid={`${prefix}-provider-fee`} type="number" min="0" step="1" value={value.provider_fee ?? ""} onChange={(event) => onChange({ ...value, provider_fee: event.target.value === "" ? null : Number(event.target.value) })} />
            <small className="field-hint">Defaults to 70% of the client fee. Internal only.</small>
          </label>
          {custom && (
            <label className="consent-checkbox drawer-form-full">
              <input data-testid={`${prefix}-save-rate`} type="checkbox" checked={value.save_to_rate_list} onChange={(event) => onChange({ ...value, save_to_rate_list: event.target.checked })} />
              <span>Save to this client’s rate list</span>
            </label>
          )}
        </>
      )}
    </>
  );
};

const emptyRate = { rate_item_id: "", custom_name: "", client_fee: null, provider_fee: null, save_to_rate_list: false };
const when = (value) => (value ? new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const ReportBox = ({ row, opened, onOpened, inReview }) => {
  const report = row.report;
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/client-evaluation-requests/${row.id}/report/download`;
  const [showVersions, setShowVersions] = useState(false);
  const markOpened = () => api.post(`/client-evaluation-requests/${row.id}/report/opened`).then(onOpened).catch(() => {});
  const download = async (version, filename) => {
    const response = await api.get(`/client-evaluation-requests/${row.id}/report/download`, { params: { mode: "download", version: version || undefined }, responseType: "blob" });
    const href = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = href;
    link.download = filename || report.original_filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
    if (!version) markOpened();
  };
  const access = report.access || {};
  const versions = report.versions || [];
  const external = row.report_external;
  return (
    <section className="report-box" data-testid="pipeline-report-box">
      <p className="eyebrow">{report.source === "external_copy" ? "REPORT · COPY OF EXTERNAL DELIVERY" : "UPLOADED REPORT"}</p>
      <div className="report-box-row">
        <FileText size={18} />
        <div>
          <strong data-testid="pipeline-report-filename">{report.original_filename}</strong>
          <small data-testid="pipeline-report-meta">Uploaded by {report.uploaded_by_name || "the provider"} · {when(report.uploaded_at)}{versions.length ? ` · current version (v${versions.length + 1})` : ""}</small>
          {inReview && <small data-testid="pipeline-report-opened">{opened ? `Opened${report.opened_by_name ? ` by ${report.opened_by_name}` : ""}${report.opened_at ? ` · ${when(report.opened_at)}` : ""}` : "Not opened yet — open it to unlock Approve"}</small>}
          {external && <small data-testid="pipeline-report-external-note">Delivered outside TAGForce: {external.note}{external.recorded_at ? ` · ${when(external.recorded_at)}` : ""}</small>}
          {row.released_at && <small data-testid="pipeline-report-released">Released to {row.client_name} · {when(row.released_at)}</small>}
          {report.file_missing_at && <small className="error-text" data-testid="pipeline-report-missing">File could not be opened in storage (checked {when(report.file_missing_at)}). Re-attach the copy.</small>}
          <small data-testid="pipeline-report-access">{access.views || access.downloads ? `Viewed ${access.views || 0} · downloaded ${access.downloads || 0}${access.last_by_name ? ` · last by ${access.last_by_name} ${when(access.last_at)}` : ""}` : "Not viewed yet"}</small>
        </div>
      </div>
      <div className="report-box-actions">
        <a className="primary-button" data-testid="pipeline-report-view" href={`${base}?mode=view`} rel="noreferrer" target="_blank" onClick={markOpened}><ExternalLink size={14} /> View</a>
        <button className="ghost-button" data-testid="pipeline-report-download" type="button" onClick={() => download()}><Download size={14} /> Download</button>
        {versions.length > 0 && <button className="ghost-button" data-testid="pipeline-report-versions-toggle" type="button" onClick={() => setShowVersions((v) => !v)}>{showVersions ? "Hide" : "Previous versions"} ({versions.length})</button>}
      </div>
      {showVersions && (
        <ul className="report-versions" data-testid="pipeline-report-versions">
          {versions.map((v) => (
            <li key={v.version} data-testid={`pipeline-report-version-${v.version}`}>
              <div>
                <strong>v{v.version} · {v.original_filename}</strong>
                <small>Uploaded by {v.uploaded_by_name || "the provider"} · {when(v.uploaded_at)} · replaced {when(v.superseded_at)}</small>
                {v.revision_comment && <small className="report-version-comment" data-testid={`pipeline-report-version-comment-${v.version}`}>Returned{v.returned_by_name ? ` by ${v.returned_by_name}` : ""}: “{v.revision_comment}”</small>}
              </div>
              <div className="report-box-actions">
                <a className="ghost-button" data-testid={`pipeline-report-version-view-${v.version}`} href={`${base}?mode=view&version=${v.version}`} rel="noreferrer" target="_blank"><ExternalLink size={13} /> View</a>
                <button className="ghost-button" data-testid={`pipeline-report-version-download-${v.version}`} type="button" onClick={() => download(v.version, v.original_filename)}><Download size={13} /> Download</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

const ExternalReportBox = ({ row, onChanged }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const external = row.report_external;
  const attach = async (event) => {
    event.preventDefault();
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const body = new FormData();
      body.append("report", file);
      await api.post(`/client-evaluation-requests/${row.id}/report/attach-external`, body, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null);
      onChanged();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="report-box report-box-external" data-testid="pipeline-external-report-box">
      <p className="eyebrow">REPORT DELIVERED OUTSIDE TAGFORCE</p>
      <div className="report-box-row">
        <FileText size={18} />
        <div>
          <strong data-testid="pipeline-external-note">{external.note}</strong>
          <small data-testid="pipeline-external-meta">Recorded by {external.recorded_by || "TAG"}{external.recorded_at ? ` · ${when(external.recorded_at)}` : ""}{row.completed_on ? ` · completed ${day(row.completed_on)}` : ""}</small>
          <small>No file is on record yet. Attach the copy that was emailed so it stays with this evaluation and in the Reports tab.</small>
        </div>
      </div>
      <form className="report-attach-form" onSubmit={attach}>
        <input data-testid="pipeline-attach-report-file" type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" onChange={(event) => setFile(event.target.files?.[0] || null)} />
        <button className="primary-button" data-testid="pipeline-attach-report-submit" type="submit" disabled={!file || busy}>{busy ? "Attaching…" : "Attach report file"}</button>
      </form>
      {error && <p className="form-message error-message" data-testid="pipeline-attach-report-error">{error}</p>}
    </section>
  );
};

export const PipelineDetail = ({ row, onClose, onChanged, clientOptions = [] }) => {
  const { user } = useAuth();
  const canSeeFees = FEE_ROLES.includes(user?.role);
  const canEditCompletedOn = ["system_administrator", "operations", "executive"].includes(user?.role);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [action, setAction] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [form, setForm] = useState({});
  const [rate, setRate] = useState(emptyRate);
  const [file, setFile] = useState(null);
  const [openedHere, setOpenedHere] = useState(false);
  const reportOpened = Boolean(row.report?.opened) || openedHere;
  const reportUploadedAt = row.report?.uploaded_at;
  useEffect(() => { setOpenedHere(false); }, [reportUploadedAt]);

  const loadDetail = useCallback(() => api.get(`/client-evaluation-requests/${row.id}`).then((response) => setDetail(response.data)).catch((requestError) => setError(formatApiError(requestError))), [row.id]);
  useEffect(() => {
    setDetail(null);
    setAction("");
    setError("");
    setOpenedHere(false);
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (action === "assign" && providers.length === 0) {
      api.get("/client-evaluation-requests/providers", { params: { evaluation_type: row.discipline } }).then((response) => setProviders(response.data)).catch(() => setProviders([]));
    }
    if ((action === "mark_completed" || action === "set_rate") && typeOptions.length === 0) {
      api.get("/client-evaluation-requests/type-options", { params: { client_id: row.client_id } }).then((response) => setTypeOptions(response.data)).catch(() => setTypeOptions([]));
    }
  }, [action, providers.length, typeOptions.length, row.discipline, row.client_id]);

  const open = (next) => {
    setAction(next);
    setError("");
    setFile(null);
    setRate(emptyRate);
    setForm(next === "mark_completed" ? { completed_on: new Date().toISOString().slice(0, 10), completed_by_name: row.provider_name || "", report_mode: "upload", external_note: "" } : next === "completed_on" ? { completed_on: row.completed_on || "", note: "" } : next === "due_date" ? { due_date: row.due_date || "", client_note: "", override_note: "" } : next === "meeting_date" ? { meeting_date: row.meeting_date || "", due_date: "", override_note: "", override: false } : next === "hold" || next === "return" || next === "upload_report" ? { note: "" } : next === "consent" ? { method: "signed_form", given_by: "", date: "", note: "" } : next === "assign" ? { provider_id: row.provider_id || "", due_date: row.due_date || "" } : { reason: "withdrawn", note: "" });
  };

  async function run(request) {
    setBusy(true);
    setError("");
    try {
      await request();
      setAction("");
      onChanged();
      loadDetail();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  const base = `/evaluation-pipeline/${row.id}`;
  const submitAction = (event) => {
    event.preventDefault();
    if (action === "mark_completed") {
      const body = new FormData();
      Object.entries(form).forEach(([key, value]) => body.append(key, value ?? ""));
      if (canSeeFees) {
        body.append("rate_item_id", rate.rate_item_id);
        body.append("custom_name", rate.custom_name);
        if (rate.client_fee !== null) body.append("client_fee", rate.client_fee);
        if (rate.provider_fee !== null) body.append("provider_fee", rate.provider_fee);
        body.append("save_to_rate_list", rate.save_to_rate_list ? "true" : "false");
      }
      if (file) body.append("report", file);
      return run(() => api.post(`${base}/mark-completed`, body, { headers: { "Content-Type": "multipart/form-data" } }));
    }
    if (action === "upload_report") {
      const body = new FormData();
      body.append("report", file);
      body.append("note", form.note || "");
      return run(() => api.post(`${base}/report`, body, { headers: { "Content-Type": "multipart/form-data" } }));
    }
    if (action === "due_date") return run(() => api.post(`${base}/due-date`, form));
    if (action === "completed_on") return run(() => api.post(`${base}/completed-on`, form));
    if (action === "meeting_date") return run(() => api.post(`${base}/meeting-date`, { meeting_date: form.meeting_date, due_date: form.override ? form.due_date : "", override_note: form.override ? form.override_note : "" }));
    if (action === "hold") return run(() => api.post(`${base}/hold`, { note: form.note }));
    if (action === "return") return run(() => api.post(`/client-evaluation-requests/${row.id}/return`, { comment: form.note }));
    if (action === "reopen") return run(() => api.post(`${base}/reopen`, { note: form.note || "" }));
    if (action === "close") return run(() => api.post(`${base}/close`, form));
    if (action === "consent") return run(() => api.post(`${base}/consent`, form));
    if (action === "assign") return run(() => api.post(`/client-evaluation-requests/${row.id}/assign-provider`, form));
    if (action === "set_rate") return run(() => api.post(`${base}/rate`, rate));
    return undefined;
  };

  const primary = row.next_step;
  const runPrimary = () => {
    if (!primary) return;
    if (primary.action === "approve") return run(() => api.post(`/client-evaluation-requests/${row.id}/complete`, {}));
    if (primary.action === "release") return run(() => api.post(`/client-evaluation-requests/${row.id}/release`, {}));
    if (primary.action === "resume") return run(() => api.post(`${base}/hold/release`, {}));
    if (primary.action === "invoice") { window.location.assign(`/evaluation-billing?month=${row.billing?.cycle_month || ""}&highlight=${row.id}`); return undefined; }
    if (primary.action === "confirm_type") return open("set_rate");
    if (["awaiting_report", "nudge"].includes(primary.action)) return undefined;
    return open(primary.action);
  };
  const nudge = row.nudge || {};
  const sendNudge = () => run(() => api.post(`/client-evaluation-requests/${row.id}/nudge`, {}));
  const nudgeTitle = !nudge.can_nudge && nudge.available_at ? `Already nudged ${when(nudge.last_nudged_at)} by ${nudge.last_nudged_by_name}. Available again ${when(nudge.available_at)}.` : undefined;
  const stepCopy = {
    approve: reportOpened ? "You have opened the provider’s report. Approve to mark this completed, or return it with a comment." : "The provider’s report is in. Open it above (View or Download) before you can approve it.",
    release: "Report approved. Releasing sends it to the school. You approve this step.",
    set_rate: "This service has no confirmed rate. Choose a rate line or enter a custom amount.",
    confirm_type: "The specific type was inferred. Confirm it to lock in the rate.",
    assign: "No provider yet. Assign one and set the due date.",
    consent: "Consent isn’t recorded. Record it before testing starts.",
    mark_completed: "Testing is done but there is no report yet. Record the completion.",
    awaiting_report: "The provider is scheduled. Nothing for you to do until the report is uploaded — or upload the finished report yourself if it reached you outside TAGForce.",
    nudge: "Assigned, not yet scheduled. Automatic reminders go to the provider 2 business days after assignment and every 3 business days after that (school closures excluded). Nudge now to send an email, portal alert and phone alert — once per 24 hours. If the finished report reached you another way, upload it here.",
    invoice: row.billing?.cycle_month ? `Ready to bill in the ${row.billing.cycle_label || row.billing.cycle_month} cycle. Opens Evaluation billing on that cycle with this line highlighted.` : "Ready to bill but not in a billing cycle yet. Opens Evaluation billing where you can assign its cycle.",
    reopen: "This request was closed. Reopen it to continue.",
    resume: "This request is on hold. Removing the hold restores its previous status and makes it visible to the school again.",
  };
  const closed = row.stage === "Closed";
  const onHold = row.status === "On hold";
  const canReopen = closed || row.status === "Completed";
  const autoDue = (meeting) => { if (!meeting) return ""; const d = new Date(`${meeting}T12:00:00`); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); };

  const actionForm = action ? (
    <form className="pipeline-action-form drawer-form-grid" data-testid={`pipeline-form-${action}`} onSubmit={submitAction}>
      {action === "mark_completed" && (
        <>
          <label><span className="field-label">Completed on</span><input data-testid="mc-completed-on" type="date" required value={form.completed_on} onChange={(event) => setForm({ ...form, completed_on: event.target.value })} /></label>
          <label><span className="field-label">Completed by</span><input data-testid="mc-completed-by" required list="mc-names" value={form.completed_by_name} onChange={(event) => setForm({ ...form, completed_by_name: event.target.value })} placeholder="Provider or Dr. Bovell" /><datalist id="mc-names"><option value="Dr. Bovell" />{row.provider_name && <option value={row.provider_name} />}</datalist></label>
          <label className="drawer-form-full"><span className="field-label">Report</span>
            <select data-testid="mc-report-mode" value={form.report_mode} onChange={(event) => setForm({ ...form, report_mode: event.target.value })}>
              <option value="upload">Upload the report</option>
              <option value="external">Report sent outside TAGForce</option>
            </select>
          </label>
          {form.report_mode === "upload" && <label className="drawer-form-full"><span className="field-label">Report file</span><input data-testid="mc-report-file" type="file" accept=".pdf,.doc,.docx,image/*" onChange={(event) => setFile(event.target.files?.[0] || null)} /></label>}
          {form.report_mode === "external" && <label className="drawer-form-full"><span className="field-label">Note</span><input data-testid="mc-external-note" required value={form.external_note} onChange={(event) => setForm({ ...form, external_note: event.target.value })} placeholder="How and when the report was sent" /></label>}
          {canSeeFees && <RateFields typeOptions={typeOptions} value={rate} onChange={setRate} prefix="mc" row={row} />}
        </>
      )}
      {action === "assign" && (
        <>
          <label><span className="field-label">Provider</span>
            <select data-testid="assign-provider" required value={form.provider_id} onChange={(event) => setForm({ ...form, provider_id: event.target.value })}>
              <option value="">Choose a provider</option>
              {providers.map((provider) => <option key={provider.id} value={provider.id}>{provider.name || `${provider.first_name} ${provider.last_name}`}{provider.discipline ? ` · ${provider.discipline}` : ""}</option>)}
            </select>
          </label>
          <label><span className="field-label">Due date</span><input data-testid="assign-due-date" type="date" required value={form.due_date} onChange={(event) => setForm({ ...form, due_date: event.target.value })} /></label>
        </>
      )}
      {action === "completed_on" && (
        <>
          <label><span className="field-label">Completed on (Eastern Time)</span><input data-testid="completed-on-input" type="date" required max={new Date().toISOString().slice(0, 10)} value={form.completed_on || ""} onChange={(event) => setForm({ ...form, completed_on: event.target.value })} /></label>
          <label className="drawer-form-full"><span className="field-label">Note (required)</span><input data-testid="completed-on-note" required minLength={3} value={form.note || ""} onChange={(event) => setForm({ ...form, note: event.target.value })} placeholder="Why the date changes — e.g. report was received Oct 24, approved Oct 27" /></label>
          <p className="pipeline-meeting-note drawer-form-full" data-testid="completed-on-hint">The completed date is the day the report was received or approved. The change is written to the history with your name.</p>
        </>
      )}
      {action === "due_date" && (
        <>
          <label><span className="field-label">New due date</span><input data-testid="due-date-input" type="date" required value={form.due_date} onChange={(event) => setForm({ ...form, due_date: event.target.value })} /></label>
          <label className="drawer-form-full"><span className="field-label">Note for the school</span><input data-testid="due-date-client-note" value={form.client_note || ""} onChange={(event) => setForm({ ...form, client_note: event.target.value })} placeholder="Optional · shown in the client portal as the reason for the revised date" /></label>
          {row.meeting_date && form.due_date && form.due_date !== autoDue(row.meeting_date) && (
            <label className="drawer-form-full"><span className="field-label">Override note (internal, required)</span><input data-testid="due-date-override-note" required value={form.override_note || ""} onChange={(event) => setForm({ ...form, override_note: event.target.value })} placeholder={`Why not ${day(autoDue(row.meeting_date))} (7 days before the meeting)?`} /></label>
          )}
        </>
      )}
      {action === "meeting_date" && (
        <>
          <label><span className="field-label">Meeting date</span><input data-testid="meeting-date-input" type="date" required value={form.meeting_date} onChange={(event) => setForm({ ...form, meeting_date: event.target.value, due_date: form.override ? form.due_date : "" })} /></label>
          <p className="pipeline-meeting-note drawer-form-full" data-testid="meeting-date-auto">{form.meeting_date ? `Report due date will be set to ${day(autoDue(form.meeting_date))} (7 days before the meeting).` : "The report due date is set automatically to 7 days before the meeting."}</p>
          <label className="consent-checkbox drawer-form-full"><input data-testid="meeting-date-override-toggle" type="checkbox" checked={form.override} onChange={(event) => setForm({ ...form, override: event.target.checked, due_date: event.target.checked ? (form.due_date || row.due_date || autoDue(form.meeting_date)) : "" })} /><span>Override the automatic due date</span></label>
          {form.override && (
            <>
              <label><span className="field-label">Report due date</span><input data-testid="meeting-date-due-input" type="date" required value={form.due_date} onChange={(event) => setForm({ ...form, due_date: event.target.value })} /></label>
              <label><span className="field-label">Override note (internal, required)</span><input data-testid="meeting-date-override-note" required value={form.override_note} onChange={(event) => setForm({ ...form, override_note: event.target.value })} placeholder="Why the report is due on a different date" /></label>
            </>
          )}
        </>
      )}
      {action === "hold" && <label className="drawer-form-full"><span className="field-label">Internal note (required)</span><input data-testid="hold-note" required minLength={3} value={form.note || ""} onChange={(event) => setForm({ note: event.target.value })} placeholder="Why this request is paused — never shown to the school" /></label>}
      {action === "return" && (
        <label className="drawer-form-full"><span className="field-label">Comment to the provider (required)</span>
          <textarea data-testid="return-comment" required minLength={3} rows={4} value={form.note || ""} onChange={(event) => setForm({ note: event.target.value })} placeholder={`What ${row.provider_name || "the provider"} needs to change before re-uploading. Sent to the provider with the returned report.`} />
        </label>
      )}
      {action === "upload_report" && (
        <>
          <p className="pipeline-meeting-note drawer-form-full" data-testid="upload-report-hint">Saves the finished report on this request{row.provider_name ? ` on behalf of ${row.provider_name}` : ""} and moves it to TAG review. You still open it before approving.</p>
          <label className="drawer-form-full"><span className="field-label">Report file (PDF or Word)</span><input data-testid="upload-report-file" type="file" required accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" onChange={(event) => setFile(event.target.files?.[0] || null)} /></label>
          <label className="drawer-form-full"><span className="field-label">Note (optional)</span><input data-testid="upload-report-note" value={form.note || ""} onChange={(event) => setForm({ note: event.target.value })} placeholder="e.g. Received from the provider by email on Oct 24" /></label>
        </>
      )}
      {action === "reopen" && <label className="drawer-form-full"><span className="field-label">Why reopen?</span><input data-testid="reopen-note" value={form.note || ""} onChange={(event) => setForm({ note: event.target.value })} placeholder="Optional note" /></label>}
      {action === "close" && (
        <>
          <label><span className="field-label">Reason</span>
            <select data-testid="close-reason" required value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })}>
              {CLOSE_REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label><span className="field-label">Note</span><input data-testid="close-note" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} placeholder={form.reason === "other" ? "Required for Other" : "Optional"} /></label>
        </>
      )}
      {action === "consent" && (
        <>
          <label><span className="field-label">Consent</span>
            <select data-testid="consent-method" value={form.method} onChange={(event) => setForm({ ...form, method: event.target.value })}>
              <option value="signed_form">Signed form (upload on the request)</option>
              <option value="verbal">Verbal from school</option>
              <option value="not_yet">Not yet</option>
            </select>
          </label>
          {form.method === "verbal" && (
            <>
              <label><span className="field-label">Who gave it</span><input data-testid="consent-given-by" required value={form.given_by} onChange={(event) => setForm({ ...form, given_by: event.target.value })} /></label>
              <label><span className="field-label">Date</span><input data-testid="consent-date" type="date" required value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} /></label>
              <label className="drawer-form-full"><span className="field-label">Note</span><input data-testid="consent-note" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} /></label>
            </>
          )}
        </>
      )}
      {action === "set_rate" && <RateFields typeOptions={typeOptions} value={rate} onChange={setRate} prefix="rate" row={row} />}
      <div className="drawer-form-full pipeline-form-actions">
        <button className="primary-button" data-testid="pipeline-form-submit" type="submit" disabled={busy || (action === "upload_report" && !file)}>{busy ? "Saving…" : action === "return" ? "Return to provider" : action === "upload_report" ? "Upload and send to TAG review" : "Save"}</button>
        <button className="ghost-button" data-testid="pipeline-form-cancel" type="button" onClick={() => setAction("")}>Cancel</button>
      </div>
    </form>
  ) : null;

  return (
    <aside className="pipeline-detail" data-testid="pipeline-detail">
      <header className="pipeline-detail-head">
        <div>
          <p className="eyebrow">{row.service_kind.toUpperCase()} · {row.client_name}</p>
          <h2 data-testid="pipeline-detail-title">{row.student_initials} · {row.service_label}</h2>
          <p className="muted">{row.student_name}{row.grade ? ` · Grade ${row.grade}` : ""}{row.provider_name ? ` · ${row.provider_name}` : " · Unassigned"}</p>
        </div>
        <div className="pipeline-detail-head-actions">
          {detail && !closed && (
            <button className="ghost-button" data-testid="pipeline-action-edit" type="button" onClick={() => setEditing(true)}><Pencil size={14} /> Edit request</button>
          )}
          <button aria-label="Close detail" className="projection-icon-button" data-testid="pipeline-detail-close" type="button" onClick={onClose}><X size={16} /></button>
        </div>
      </header>
      {editing && detail && (
        <EditorModal onClose={() => setEditing(false)} testId="edit-evaluation-modal">
          <StaffEvaluationRequestForm
            clientOptions={clientOptions}
            initial={detail}
            onClose={() => setEditing(false)}
            onCreated={() => { setEditing(false); onChanged(); loadDetail(); }}
          />
        </EditorModal>
      )}
      <StageBar stageIndex={row.stage_index} stage={row.stage} testId="pipeline-detail-stage-bar" />
      <p className="stage-note" data-testid="pipeline-detail-stage">{row.stage === "Closed" ? row.stage_note : `${row.stage}${row.stage_note ? ` · ${row.stage_note}` : ""}`}{row.days_late > 0 ? ` · ${row.days_late} d late` : ""}</p>
      {error && <p className="form-message error-message" data-testid="pipeline-detail-error">{error}</p>}
      {row.report?.has_file && <ReportBox row={row} opened={reportOpened} inReview={row.stage === "TAG review"} onOpened={() => { setOpenedHere(true); onChanged(); }} />}
      {!row.report?.has_file && row.report_external && <ExternalReportBox row={row} onChanged={onChanged} />}
      {primary && (
        <section className="next-step-box" data-testid="pipeline-next-step">
          <p className="eyebrow">NEXT STEP</p>
          <h3>{primary.label}</h3>
          <p>{stepCopy[primary.action]}</p>
          {["awaiting_report", "nudge"].includes(primary.action) && (
            <div className="next-step-actions">
              <button className="ghost-button on-dark" data-testid="pipeline-next-step-upload-report" type="button" disabled={busy} onClick={() => open("upload_report")}>Upload finished report</button>
            </div>
          )}
          {!["awaiting_report", "nudge"].includes(primary.action) && (
            <div className="next-step-actions">
              <button className="primary-button" data-testid="pipeline-next-step-button" type="button" disabled={busy || (primary.action === "approve" && !reportOpened)} title={primary.action === "approve" && !reportOpened ? "Open the report first" : undefined} onClick={runPrimary}>{primary.label}</button>
              {primary.action === "approve" && <button className="ghost-button on-dark" data-testid="pipeline-return-button" type="button" disabled={busy} onClick={() => open("return")}>Return for revision</button>}
            </div>
          )}
        </section>
      )}
      {(action === "return" || action === "upload_report") && actionForm}
      {nudge.eligible && (
        <section className="nudge-box" data-testid="pipeline-nudge-box">
          <div>
            <strong>Provider hasn't scheduled yet</strong>
            <p className="next-step-meta" data-testid="pipeline-nudge-status">
              {nudge.last_nudged_at
                ? `Last nudged ${when(nudge.last_nudged_at)} by ${nudge.last_nudged_by_name} · ${nudge.count} sent${nudge.automatic_count ? `, ${nudge.automatic_count} automatic` : ""}${nudge.available_at ? ` · next available ${when(nudge.available_at)}` : ""}`
                : "Not nudged yet. Automatic reminders start 2 business days after assignment."}
            </p>
          </div>
          <button className="primary-button" data-testid="pipeline-nudge-button" type="button" disabled={busy || !nudge.can_nudge} title={nudgeTitle} onClick={sendNudge}><BellRing size={14} /> {busy ? "Sending…" : "Nudge provider"}</button>
        </section>
      )}
      <Panel title="Service ordered" testId="pipeline-service-panel">
        <dl className="detail-grid">
          <div><dt>Kind</dt><dd data-testid="pipeline-service-kind">{row.service_kind}</dd></div>
          <div><dt>Service</dt><dd>{row.service_label}</dd></div>
          <div><dt>Discipline</dt><dd>{row.discipline}</dd></div>
          <div><dt>Meeting</dt><dd data-testid="pipeline-detail-meeting">{row.meeting_date ? day(row.meeting_date) : "Not set"}</dd></div>
          <div><dt>Due</dt><dd data-testid="pipeline-detail-due">{day(row.due_date)}{row.meeting_date ? (row.due_date_source === "override" ? " · override" : " · 7 days before the meeting") : ""}</dd></div>
          {row.due_date_source === "override" && row.due_date_override_note && <div><dt>Override</dt><dd data-testid="pipeline-detail-override-note">{row.due_date_override_note}</dd></div>}
          <div><dt>Rate</dt><dd data-testid="pipeline-rate">{row.rate.status === "confirmed" ? (canSeeFees && row.rate.client_fee ? `${money(row.rate.client_fee)} · confirmed` : "Confirmed") : `${row.rate.status} · ${row.rate.hint}`}</dd></div>
          {canSeeFees && detail?.billing?.provider_fee !== undefined && detail?.billing?.provider_fee !== null && <div><dt>Provider fee</dt><dd data-testid="pipeline-provider-fee">{money(detail.billing.provider_fee)}</dd></div>}
          {row.completed_by_name && <div><dt>Completed by</dt><dd>{row.completed_by_name}</dd></div>}
          {(row.completed_on || row.status === "Completed") && (
            <div><dt>Completed on</dt><dd data-testid="pipeline-completed-on">{row.completed_on ? day(row.completed_on) : "Not recorded"}{row.completed_on_corrected ? ` · corrected by ${row.completed_on_corrected.by_name}` : ""}{canEditCompletedOn && <> · <button type="button" className="subtle-link" data-testid="pipeline-action-completed-on" onClick={() => open("completed_on")}>Edit</button></>}</dd></div>
          )}
          {row.report_external && <div><dt>Report</dt><dd>Sent outside TAGForce{row.report_external.note ? ` · ${row.report_external.note}` : ""}</dd></div>}
          {row.linked_group_id && <div><dt>Linked</dt><dd>Part of a multi-service request</dd></div>}
        </dl>
      </Panel>
      <Panel title="Consent" testId="pipeline-consent-panel">
        <p data-testid="pipeline-consent-summary">{row.consent ? `${row.consent.label}${row.consent.given_by ? ` · ${row.consent.given_by}` : ""}${row.consent.date ? ` · ${day(row.consent.date)}` : ""}` : detail?.consent_document ? `Signed form · ${detail.consent_document.original_filename}` : "Not yet recorded"}</p>
        {!closed && <button className="ghost-button" data-testid="pipeline-action-consent" type="button" onClick={() => open("consent")}>Record consent</button>}
      </Panel>
      {onHold && row.hold && (
        <section className="hold-box" data-testid="pipeline-hold-box">
          <strong>On hold since {day(row.hold.at)} · {row.hold.by_name}</strong>
          <p>{row.hold.note}</p>
          <p>Hidden from the school's portal and excluded from overdue alerts. Previous status: {row.hold.previous_status}.</p>
        </section>
      )}
      {!closed && !onHold && (
        <Panel title="Actions" testId="pipeline-actions-panel">
          <div className="pipeline-actions">
            <button className="ghost-button" data-testid="pipeline-action-mark-completed" type="button" onClick={() => open("mark_completed")}>Mark completed</button>
            {row.status !== "Completed" && <button className="ghost-button" data-testid="pipeline-action-upload-report" type="button" onClick={() => open("upload_report")}>{row.report ? "Replace report" : "Upload finished report"}</button>}
            <button className="ghost-button" data-testid="pipeline-action-reassign" type="button" onClick={() => open("assign")}>{row.provider_id ? "Reassign provider" : "Assign provider"}</button>
            <button className="ghost-button" data-testid="pipeline-action-meeting-date" type="button" onClick={() => open("meeting_date")}>{row.meeting_date ? "Change meeting date" : "Set meeting date"}</button>
            <button className="ghost-button" data-testid="pipeline-action-due-date" type="button" onClick={() => open("due_date")}>Change due date</button>
            {canSeeFees && row.stage !== "Billed" && <button className="ghost-button" data-testid="pipeline-action-rate" type="button" onClick={() => open("set_rate")}>{row.rate.status === "confirmed" ? "Change rate" : "Set rate"}</button>}
            {canReopen && <button className="ghost-button" data-testid="pipeline-action-reopen" type="button" onClick={() => open("reopen")}>Reopen</button>}
            {row.status !== "Completed" && <button className="ghost-button" data-testid="pipeline-action-hold" type="button" onClick={() => open("hold")}>Place on hold</button>}
            <button className="ghost-button danger" data-testid="pipeline-action-close" type="button" onClick={() => open("close")}>Close without completing</button>
          </div>
        </Panel>
      )}
      {onHold && (
        <Panel title="Actions" testId="pipeline-actions-panel">
          <div className="pipeline-actions">
            <button className="primary-button" data-testid="pipeline-action-remove-hold" type="button" disabled={busy} onClick={() => run(() => api.post(`${base}/hold/release`, {}))}>Remove hold</button>
            <button className="ghost-button danger" data-testid="pipeline-action-close" type="button" onClick={() => open("close")}>Close without completing</button>
          </div>
        </Panel>
      )}
      {closed && <Panel title="Actions" testId="pipeline-actions-panel"><button className="ghost-button" data-testid="pipeline-action-reopen" type="button" onClick={() => open("reopen")}>Reopen</button></Panel>}
      {action && action !== "return" && action !== "upload_report" && actionForm}
      <Panel title="History" testId="pipeline-history-panel">
        <ul className="pipeline-history">
          {(detail?.status_history || []).slice().reverse().slice(0, 12).map((entry, index) => (
            <li key={`${entry.occurred_at}-${index}`}><strong>{entry.detail}</strong><small>{entry.status ? `${entry.status} · ` : ""}{entry.actor_name ? `${entry.actor_name} · ` : ""}{entry.occurred_at ? new Date(entry.occurred_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</small></li>
          ))}
          {!detail && <li><span>Loading history…</span></li>}
        </ul>
      </Panel>
    </aside>
  );
};
